import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory, useParams } from 'react-router-dom';
import { makeStyles } from '@material-ui/styles';
import { Button, Card, CardContent, CardHeader } from '@material-ui/core';
import { segmentedListTitles } from 'constants.js';
import { CloseIcon, TableWithIcons, Toolbar } from 'components';
import {
  resetSnackbar,
  deleteSegmentedList,
  getPaginatedSegmentedLists,
  getList
} from 'store/lists/actions';
import { actions as userMessageActions } from 'store/userMessaging';
import { dismissButton, dispatchButton } from 'helpers/error/buttonBuilders';
import UserMessageSnackbar from 'views/UserMessaging/customModalContent/userMessageSnackbar';
import { listHelpers } from 'store/helpers/tableAndToolbar';
import { Alert } from '@material-ui/lab';

const useStyles = makeStyles(theme => ({
  root: {
    padding: theme.spacing(3)
  },
  headerContainer: {
    display: 'flex',
    width: '100%',
    justifyContent: 'space-between'
  },
  title: {
    paddingBottom: '2px',
    width: '95%'
  },
  backButton: {
    marginBottom: '16px',
    marginLeft: '16px'
  },
  content: {
    paddingBottom: '8px'
  }
}));

const SegmentedLists = () => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const history = useHistory();
  const { listId } = useParams();

  const {
    isTemplateReadOnly,
  } = listHelpers();

  const gettingSegmentedList = useSelector(
    store => store.lists.segmentedList.getting
  );
  const segmentedLists = useSelector(
    store => store.lists.segmentedList.listsData
  );
  const showSnackbar = useSelector(store => store.lists.showSnackbar);
  const snackbarMessage = useSelector(store => store.lists.snackbarMessage);
  const snackbarSeverity = useSelector(store => store.lists.snackbarSeverity);
  const isSegmentedListDeleted = useSelector(
    store => store.lists.segmentedList.deleted
  );
  const currentList = useSelector(store => store.lists.subscribersList.currentList);
  const gettingSubscribers = useSelector(store => store.lists.profileList.getting);

  const [snackbarInfo, setSnackBarInfo] = useState({ show: false });

  useEffect(() => {
    dispatch(resetSnackbar());
    dispatch(getList(listId));
    dispatch(getPaginatedSegmentedLists(listId));
  }, []);

  useEffect(() => {
    if (showSnackbar) {
      setSnackBarInfo({
        show: true,
        message: snackbarMessage,
        severity: snackbarSeverity
      });
    } else {
      setSnackBarInfo({
        show: false,
        message: ''
      });
    }
  }, [showSnackbar]);

  useEffect(() => {
    if (isSegmentedListDeleted) {
      dispatch(getPaginatedSegmentedLists(listId));
    }
  }, [isSegmentedListDeleted]);

  const getButtonsGroup = () =>
    !isTemplateReadOnly(currentList, gettingSubscribers) ? [
      {
        name: 'Add Segmented List',
        action: () => {
          history.push(`/lists/${listId}/segmented-list/settings`);
        }
      }
    ] : [];

  const onClickDelete = segmentedListId => {
    dispatch(
      userMessageActions.setMessage({
        title: 'Delete Segmented list',
        severity: 'warning',
        instructions: `Are you sure you want to delete this segmented list?`,
        buttons: [
          dispatchButton('Delete', deleteSegmentedList(segmentedListId)),
          dismissButton('Cancel')
        ]
      })
    );
  };

  const onClickEditSegmentedList = id => {
    history.push(`/lists/${listId}/segmented-list/${id}/settings`);
  };

  const getActionButtonOptions = id =>
    !isTemplateReadOnly(currentList, gettingSubscribers) ? [
      {
        id: 'edit',
        title: 'Edit',
        action: () => onClickEditSegmentedList(id)
      },
      {
        id: 'delete',
        title: 'Delete',
        action: () => onClickDelete(id)
      }
    ] : [];

  const navBack = () => {
    history.push('/lists');
  };

  return (
    <div className={classes.root}>
      <div className={classes.content}>
        {isTemplateReadOnly(currentList, !currentList.name) &&
          <Alert severity="info">
            You can only view this list and do not have access to
            edit. Tenant Default list can be edited only with 'ALL' property.
          </Alert>}
        <Card>
          <div className={classes.headerContainer}>
            <CardHeader
              title="Segmented List"
              titleTypographyProps={{ variant: 'h3' }}
              className={classes.title}
            />
            <CloseIcon onClickClose={navBack} />
          </div>
          <CardContent>
            <Toolbar
              searchPlaceholder={'Search Segmented list'}
              buttonsGroup={getButtonsGroup()}
              action={getPaginatedSegmentedLists(listId)}
              searchInputSize="small"
            />
            <TableWithIcons
              isLoading={gettingSegmentedList}
              tableTitles={segmentedListTitles}
              onDoubleClickRow={row => onClickEditSegmentedList(row.id)}
              actionButtonOptions={row => getActionButtonOptions(row.id)}
              filteredRows={segmentedLists}
              action={getPaginatedSegmentedLists(listId)}
            />
          </CardContent>
          <Button
            className={classes.backButton}
            color="secondary"
            variant="contained"
            onClick={navBack}>
            Back
          </Button>
        </Card>
      </div>

      {showSnackbar && (
        <UserMessageSnackbar
          openSnackbar={snackbarInfo.show}
          message={snackbarInfo.message}
          severity={snackbarInfo.severity}
          onClose={() => dispatch(resetSnackbar())}
        />
      )}
    </div>
  );
};

export default SegmentedLists;
